import { useEffect, useRef } from 'react'
import { useAuthStore } from '../../../store/authStore'
import { useSceneStore } from '../sceneStore'

export function useSceneFromAgent(latestUiAction, sessionId, panelDrafts) {
  const { panel, panelPayload, openPanel, closePanel, panelDismissed, resetPanelDismissed } = useSceneStore()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const prevDraftCount = useRef(0)
  const prevSessionId = useRef(null)
  const draftFingerprintRef = useRef('')

  const activeUiAction = isAuthenticated && latestUiAction?.type === 'auth_redirect'
    ? null
    : latestUiAction

  const draftDismissed = panelDismissed.draft === true

  useEffect(() => {
    if (sessionId !== prevSessionId.current) {
      prevDraftCount.current = 0
      draftFingerprintRef.current = ''
      prevSessionId.current = sessionId
      resetPanelDismissed('draft')
    }
  }, [sessionId, resetPanelDismissed])

  useEffect(() => {
    const fingerprint = panelDrafts.map((draft) => draft.id).join(',')
    if (!fingerprint) return
    if (fingerprint !== draftFingerprintRef.current) {
      if (draftFingerprintRef.current) resetPanelDismissed('draft')
      draftFingerprintRef.current = fingerprint
    }
  }, [panelDrafts, resetPanelDismissed])

  useEffect(() => {
    // Auto-close only agent-driven panels once their pending action is gone.
    // Do not auto-close auth: chrome "Sign in" opens it without auth_redirect.
    // Do not auto-close context: chrome import mode opens without context_select.
    if (panel === 'oauth' && activeUiAction?.type !== 'oauth_connect') {
      closePanel()
    }
    if (panel === 'choice' && activeUiAction?.type !== 'user_choice') {
      closePanel()
    }
    if (
      panel === 'context'
      && activeUiAction?.type !== 'context_select'
      && panelPayload?.mode !== 'import'
    ) {
      closePanel()
    }
    if (panel === 'draft' && panelDrafts.length === 0 && activeUiAction?.type !== 'draft_review') {
      closePanel()
    }
  }, [panel, panelPayload, activeUiAction, panelDrafts.length, closePanel])

  useEffect(() => {
    // Auth panel is closed explicitly on success; also clear if session appears while open.
    if (panel === 'auth' && isAuthenticated) {
      closePanel()
    }
  }, [panel, isAuthenticated, closePanel])

  useEffect(() => {
    if (!activeUiAction) return

    if (activeUiAction.type === 'auth_redirect') {
      const mode = activeUiAction.mode === 'sign_up' ? 'signup' : 'login'
      openPanel('auth', {
        mode,
        payload: {
          reason: activeUiAction.reason,
          returnTo: activeUiAction.return_to || (sessionId ? `/app/s/${sessionId}` : '/app'),
        },
      })
    } else if (activeUiAction.type === 'collect_sensitive_input') {
      openPanel('sensitive', { payload: activeUiAction })
    } else if (activeUiAction.type === 'oauth_connect') {
      openPanel('oauth', { payload: activeUiAction })
    } else if (activeUiAction.type === 'user_choice') {
      openPanel('choice', { payload: activeUiAction })
    } else if (activeUiAction.type === 'context_select') {
      openPanel('context', { payload: activeUiAction })
    } else if (activeUiAction.type === 'draft_review') {
      if (draftDismissed) return
      const drafts = panelDrafts.length > 0 ? panelDrafts : (activeUiAction.drafts || [])
      if (drafts.length > 0) {
        openPanel('draft', { payload: { drafts } })
      }
    }
  }, [activeUiAction, sessionId, panelDrafts, openPanel, draftDismissed])

  useEffect(() => {
    if (draftDismissed) return
    if (panelDrafts?.length > prevDraftCount.current && panel !== 'draft') {
      openPanel('draft', { payload: { drafts: panelDrafts } })
    }
    prevDraftCount.current = panelDrafts?.length || 0
  }, [panelDrafts, panelDrafts?.length, openPanel, panel, draftDismissed])

  useEffect(() => {
    if (draftDismissed) return
    const hasPublished = panelDrafts.some((draft) => draft.published)
    if (hasPublished && panel !== 'draft' && panelDrafts.length > 0) {
      openPanel('draft', { payload: { drafts: panelDrafts } })
    }
  }, [panelDrafts, openPanel, panel, draftDismissed])
}
